import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { colors } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, ImageIcon } from '@gitbook/spine-react';

import { confluence, googleDocs } from './icons';

export interface ImportSource {
    name: string;
    title: string;
    subtitle?: string;
    fileExtension?: string;
    fileIcon?: IconDefinition;
    source: api.ImportContentSource;
    icon: IconDefinition;
    type: 'application' | 'file';

    /** Descriptions of instructions for this source. */
    steps?: React.ReactNode[];

    /**
     * Maximum number of files that can be uploaded at once.
     */
    fileUploadsLimit?: number;
}

/*
 * All possible import sources
 */
export function useImportSources(): ImportSource[] {
    return [
        {
            name: 'Website',
            title: 'Import a Website',
            subtitle: 'Enter a link to a web page below to import it into your space.',
            source: api.ImportContentSource.Website,
            icon: <Icon.FileCode className="text-primary" />,
            type: 'application',
        },
        {
            name: 'Markdown',
            title: 'Import a Markdown file',
            source: api.ImportContentSource.Markdown,
            icon: <Icon.FileText style={{ color: colors.scarlet.light }} />,
            subtitle: 'Drag and drop your .md file below to start the import.',
            fileIcon: Icon.FileText,
            fileExtension: '.md',
            type: 'file',
        },
        {
            name: 'MS Word',
            title: 'Import a Word document',
            source: api.ImportContentSource.Docx,
            icon: <Icon.FileWord style={{ color: colors.primary.light }} />,
            subtitle: 'Drag and drop your .docx file below to start the import.',
            fileIcon: Icon.FileWord,
            fileExtension: '.docx',
            type: 'application',
        },
        {
            name: 'HTML',
            title: 'Import a HTML file',
            source: api.ImportContentSource.Html,
            subtitle: 'Drag and drop your .html file below to start the import.',
            icon: <Icon.FileCode style={{ color: colors.violet.light }} />,
            fileIcon: Icon.FileCode,
            fileExtension: '.html',
            type: 'file',
        },
        {
            name: 'ZIP',
            title: 'Import a ZIP file',
            source: api.ImportContentSource.Zip,
            subtitle: 'Drag and drop your .zip file below to start the import.',
            icon: <Icon.FileZIP style={{ color: colors.orange.light }} />,
            fileIcon: Icon.FileZIP,
            fileExtension: '.zip',
            type: 'file',
        },
        {
            name: 'Confluence',
            title: 'Import from Confluence',
            icon: <ImageIcon src={confluence} />,
            source: api.ImportContentSource.Confluence,
            type: 'application',
            subtitle: 'Export your space as ZIP to bring your content from Confluence.',
            fileExtension: '.zip',
            fileIcon: Icon.FileZIP,
            steps: [
                <p>
                    Go to <strong>Space settings</strong> for the space you're looking to export and
                    select the <strong>Content tools</strong> section.
                </p>,
                <p>
                    Click on the <strong>export</strong> tab, and select <strong>HTML</strong>, then
                    hit <strong>next</strong>, and <strong>export</strong>.
                </p>,
                <p>
                    Choose <strong>normal export</strong> unless you want to choose specific pages
                    to export.
                </p>,
            ],
        },
        {
            name: 'Notion',
            title: 'Import from Notion',
            icon: <Icon.Notion />,
            source: api.ImportContentSource.Notion,
            type: 'application',
            subtitle: 'Export your workspace as Markdown to bring your content from Notion.',
            steps: [
                <p>
                    Go to the Workspace you're looking to export and select the <strong>…</strong>{' '}
                    button in the top right.
                </p>,
                <p>
                    Choose <strong>Export all as Markdown</strong>
                </p>,
            ],
            fileIcon: Icon.FileText,
            fileExtension: '.md',
        },
        {
            name: 'GitHub Wiki',
            title: 'Import from GitHub Wiki',
            subtitle: 'Enter a link to a GitHub Wiki below to import it into your space.',
            icon: <Icon.GitHub />,
            source: api.ImportContentSource.GithubWiki,
            type: 'application',
        },
        {
            name: 'Google docs',
            title: 'Import from Google Docs',
            icon: <ImageIcon src={googleDocs} />,
            source: api.ImportContentSource.GoogleDocs,
            type: 'application',
            subtitle:
                'Export your documents as .docx or webpage to bring your content from Google Docs.',
            steps: [
                <p>
                    Click on <strong>File {'>'} Download</strong>.
                </p>,
                <p>
                    Choose between <strong>Microsoft Word</strong> or <strong>Web Page</strong>.
                </p>,
            ],
            fileIcon: Icon.FileWord,
            fileExtension: '.docx',
        },
    ];
}
